import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from '../../../icons';
import { getDateLocale } from '../../../lib/base-i18n';
import { addDays, addMonths, isSameDay, isWithin, monthGrid, shiftMonthKeepDay, startOfDay, toDayKey } from './calendar-core';
import './Calendar.css';

/**
 * Calendar — 달 격자 하나 (디자인 정본 C1 · C5).
 * `DatePicker` · `DateRangePicker` 가 이 위에 선다.
 *
 * ★★ **격자는 항상 7×6(42칸) 고정**이다. 5주로 끝나는 달에 5주만 그리면
 *    달을 넘길 때 팝오버 높이가 껑충 뛴다 — 누르려던 자리가 손 밑에서 움직인다.
 *
 * ★★ **키보드로 달력을 걸어 다닐 수 있어야 한다**(정본 C1) —
 *    ←→ 하루 · ↑↓ 한 주 · PageUp/PageDown 한 달 · Home/End 그 주의 처음/끝.
 *    `role="grid"` 와 `aria-live` 로 지금 보고 있는 달을 알린다.
 *
 * ★ **요일 이름과 달 이름은 만들지 않는다** — `Intl` 이 로케일에 맞춰 준다.
 *   로케일은 `getDateLocale()`(base-i18n SSOT)을 거친다. 못 박으면 레포의 검사가 막는다.
 */
export interface CalendarProps {
  /** 보고 있는 달. 그 달의 아무 날짜나 줘도 된다. */
  month: Date;
  onMonthChange: (next: Date) => void;
  /** 고른 날(들). 하나면 단일, 둘이면 기간의 양 끝. */
  selected?: readonly Date[];
  /** 기간 미리보기 — 시작만 고른 상태에서 손이 지나가는 날. */
  hovered?: Date | null;
  onHover?: (d: Date | null) => void;
  onSelect: (d: Date) => void;
  min?: Date | null;
  max?: Date | null;
  /** 고를 수 없는 날 — 휴무일 등. `YYYY-MM-DD`. */
  disabledDays?: ReadonlySet<string>;
  /** 붉게 그릴 날 — 공휴일. `YYYY-MM-DD`. */
  holidays?: ReadonlySet<string>;
  /** 아래 점을 찍을 날 — 일정이 있는 날. */
  markedDays?: ReadonlySet<string>;
  /** 0=일요일 시작(지금 캘린더 화면과 같다) · 1=월요일 시작. */
  weekStart?: 0 | 1;
  /** 달 이동 단추를 감춘다 — 두 달을 나란히 놓을 때 가운데 것들. */
  hideNav?: 'prev' | 'next' | 'both';
  /** 머리에 덧붙일 것. */
  headerExtra?: ReactNode;
  className?: string;
}

export function Calendar({
  month,
  onMonthChange,
  selected = [],
  hovered = null,
  onHover,
  onSelect,
  min,
  max,
  disabledDays,
  holidays,
  markedDays,
  weekStart = 0,
  hideNav,
  headerExtra,
  className,
}: CalendarProps) {
  const locale = getDateLocale();
  const days = useMemo(() => monthGrid(month, weekStart), [month, weekStart]);
  const today = startOfDay(new Date());

  /** 키보드가 짚고 있는 날. 화살표로 움직이고 Enter 로 고른다. */
  const [focusDay, setFocusDay] = useState<Date | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const shouldFocusRef = useRef(false);

  useEffect(() => {
    if (!shouldFocusRef.current || !focusDay) return;
    shouldFocusRef.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${toDayKey(focusDay)}"]`)?.focus();
  }, [focusDay, month]);

  const weekNames = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 1970-01-04 는 일요일 — 거기서부터 weekStart 만큼 민다.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(1970, 0, 4 + ((i + weekStart) % 7)))));
  }, [locale, weekStart]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' }).format(month),
    [locale, month],
  );

  const selectedKeys = new Set(selected.map(toDayKey));
  const range = selected.length === 2 ? [selected[0]!, selected[1]!] : null;
  /** 시작만 고른 상태에서 손이 지나가는 곳까지 옅게 칠한다. */
  const preview = selected.length === 1 && hovered ? [selected[0]!, hovered] : null;
  const band = range ?? preview;

  const inBand = (d: Date) => {
    if (!band) return false;
    const [b0, b1] = [band[0]!, band[1]!];
    const [a, b] = b0.getTime() <= b1.getTime() ? [b0, b1] : [b1, b0];
    const t = startOfDay(d).getTime();
    return t > startOfDay(a).getTime() && t < startOfDay(b).getTime();
  };

  const dayDisabled = (d: Date) => !isWithin(d, min, max) || (disabledDays?.has(toDayKey(d)) ?? false);

  const move = (base: Date, delta: number, unit: 'day' | 'month') => {
    // ★ 달 이동에 `addMonths` 를 쓰면 **날짜를 잃고 1일로 간다** — 초점을 옮기는 자리라 날짜를 지킨다.
    const next = unit === 'day' ? addDays(base, delta) : shiftMonthKeepDay(base, delta);
    // 달을 넘어가면 보고 있는 달도 함께 옮긴다 — 안 그러면 초점이 안 보이는 칸으로 간다.
    if (next.getMonth() !== month.getMonth() || next.getFullYear() !== month.getFullYear()) {
      onMonthChange(new Date(next.getFullYear(), next.getMonth(), 1));
    }
    shouldFocusRef.current = true;
    setFocusDay(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, day: Date) => {
    const map: Record<string, () => void> = {
      ArrowLeft: () => move(day, -1, 'day'),
      ArrowRight: () => move(day, 1, 'day'),
      ArrowUp: () => move(day, -7, 'day'),
      ArrowDown: () => move(day, 7, 'day'),
      PageUp: () => move(day, -1, 'month'),
      PageDown: () => move(day, 1, 'month'),
      Home: () => move(day, -((day.getDay() - weekStart + 7) % 7), 'day'),
      End: () => move(day, 6 - ((day.getDay() - weekStart + 7) % 7), 'day'),
    };
    const fn = map[e.key];
    if (fn) {
      e.preventDefault();
      fn();
    }
  };

  return (
    <div className={['wc-cal', className ?? ''].filter(Boolean).join(' ')}>
      <div className="wc-cal__head">
        {hideNav !== 'prev' && hideNav !== 'both' ? (
          <button type="button" className="wc-cal__nav" onClick={() => onMonthChange(addMonths(month, -1))}
            aria-label="이전 달">
            <ChevronLeft aria-hidden />
          </button>
        ) : <span className="wc-cal__nav-space" />}
        {/* 지금 보고 있는 달을 소리로도 알린다 — 화살표로 달을 넘겼을 때 화면만 바뀌면 모른다. */}
        <span className="wc-cal__title" aria-live="polite">{monthLabel}</span>
        {hideNav !== 'next' && hideNav !== 'both' ? (
          <button type="button" className="wc-cal__nav" onClick={() => onMonthChange(addMonths(month, 1))}
            aria-label="다음 달">
            <ChevronRight aria-hidden />
          </button>
        ) : <span className="wc-cal__nav-space" />}
      </div>
      {headerExtra}

      <div className="wc-cal__grid" role="grid" aria-label={monthLabel} ref={gridRef}>
        <div className="wc-cal__row wc-cal__row--head" role="row">
          {weekNames.map((w, i) => {
            const dow = (i + weekStart) % 7;
            return (
              <span key={w + i} role="columnheader" aria-label={w}
                className={['wc-cal__wd', dow === 0 ? 'is-sun' : '', dow === 6 ? 'is-sat' : ''].filter(Boolean).join(' ')}>
                {w}
              </span>
            );
          })}
        </div>

        {Array.from({ length: 6 }, (_, w) => (
          <div className="wc-cal__row" role="row" key={w}>
            {days.slice(w * 7, w * 7 + 7).map((d) => {
              const key = toDayKey(d);
              const outside = d.getMonth() !== month.getMonth();
              const isSelected = selectedKeys.has(key);
              const disabled = dayDisabled(d);
              const dow = d.getDay();
              const holiday = holidays?.has(key) ?? false;
              /** 초점 후보 — 고른 날 > 오늘 > 그 달 1일. 한 격자에 하나만 tabbable 이어야 한다. */
              const tabTarget = focusDay
                ? isSameDay(d, focusDay)
                : isSelected || (selectedKeys.size === 0 && isSameDay(d, today) && !outside)
                  || (selectedKeys.size === 0 && !days.some((x) => isSameDay(x, today) && x.getMonth() === month.getMonth())
                      && d.getDate() === 1 && !outside);
              const cls = [
                'wc-cal__day',
                outside ? 'is-outside' : '',
                isSelected ? 'is-selected' : '',
                !isSelected && isSameDay(d, today) ? 'is-today' : '',
                inBand(d) ? 'is-inband' : '',
                holiday || dow === 0 ? 'is-sun' : '',
                !holiday && dow === 6 ? 'is-sat' : '',
                markedDays?.has(key) ? 'is-marked' : '',
              ].filter(Boolean).join(' ');
              return (
                <button
                  key={key}
                  type="button"
                  role="gridcell"
                  data-day={key}
                  className={cls}
                  aria-selected={isSelected}
                  aria-current={isSameDay(d, today) ? 'date' : undefined}
                  disabled={disabled}
                  tabIndex={tabTarget ? 0 : -1}
                  onClick={() => onSelect(d)}
                  onMouseEnter={() => onHover?.(d)}
                  onFocus={() => setFocusDay(d)}
                  onKeyDown={(e) => onKeyDown(e, d)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
